import store from '../redux/store';
import oktaAuth from '../services/configs/oktaAuth';
import { logout, toggleSessionExpiredModal } from '../features/auth/authSlice';
import { resetAdviceAdminReducer } from '../features/adviceAdmin';
import { advisorConsoleActions } from '../features/advisorConsole';
import { initializeAdviceClient } from '../features/client';
import { initializeGoals } from '../features/client-goals';
import { resetEws } from '../features/ews';

const AUTH_CHANNEL_NAME = 'advice-auth';
const SESSION_EXPIRED_LOGOUT = 'session-expired-logout';
const MANUAL_LOGOUT = 'manual-logout';
const USER_ACTIVITY = 'user-activity';

// Single BroadcastChannel shared across the app so that a logout in one tab
// propagates to every other open tab of the application.
export const authChannel: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_CHANNEL_NAME) : null;

let sessionExpiredLogoutInFlight = false;

const clearAllSliceState = () => {
    store.dispatch(advisorConsoleActions.resetAdviceConsoleReducer());
    store.dispatch(initializeAdviceClient());
    store.dispatch(initializeGoals());
    store.dispatch(resetEws());
    store.dispatch(resetAdviceAdminReducer());
    store.dispatch(logout());
};

/**
 * Subscribe to cross-tab logout broadcasts. Returns an unsubscribe function.
 *
 * @param onExpired Fires when another tab triggers a session-expired logout
 *                  (shows the "Session expired" modal on the login page).
 * @param onManual  Fires when another tab triggers a user-initiated logout
 *                  (silent redirect, no session-expired messaging).
 */
export const onCrossTabLogout = (onExpired: () => void, onManual: () => void): (() => void) => {
    if (!authChannel) return () => {};
    const listener = (event: MessageEvent) => {
        if (event?.data?.type === SESSION_EXPIRED_LOGOUT) {
            console.log('[session-auth] Cross-tab session-expired broadcast received - following logout in this tab');
            onExpired();
        }
        if (event?.data?.type === MANUAL_LOGOUT) {
            console.log('[session-auth] Cross-tab manual logout broadcast received - redirecting this tab');
            onManual();
        }
    };
    authChannel.addEventListener('message', listener);
    return () => authChannel.removeEventListener('message', listener);
};

/**
 * Notify other open tabs that the user manually logged out so they can follow.
 * Used by the manual logout flow (`useLogoutHandler`) which performs its own
 * Okta sign-out but still needs sibling tabs to redirect. Unlike a
 * session-expired logout, this must NOT surface the "Session expired" modal.
 */
export const broadcastLogout = () => {
    console.log('[session-auth] Broadcasting manual logout to other tabs');
    authChannel?.postMessage({ type: MANUAL_LOGOUT });
};

/**
 * Notify other open tabs that the user is active in this tab. The inactivity
 * timeout is tracked per tab, so without this an idle tab would time out and
 * idle timer (and dismiss the timeout warning) when they receive this, so the
 * session only expires when the user is inactive across every open tab.
 */
export const broadcastUserActivity = () => {
    authChannel?.postMessage({ type: USER_ACTIVITY });
};

/**
 * Subscribe to cross-tab user-activity broadcasts. Returns an unsubscribe
 * function. Fires when the user interacts with another open tab so this tab can
 * keep its inactivity timer alive.
 */
export const onCrossTabActivity = (onActivity: () => void): (() => void) => {
    if (!authChannel) return () => {};
    const listener = (event: MessageEvent) => {
        if (event?.data?.type === USER_ACTIVITY) {
            onActivity();
        }
    };
    authChannel.addEventListener('message', listener);
    return () => authChannel.removeEventListener('message', listener);
};

/**
 * Logs the user out and surfaces the existing "Session expired" modal on the
 *
 * @param broadcast When true (default) other tabs are notified to log out too.
 *                  Set to false when handling a broadcast from another tab to
 *                  avoid an infinite echo.
 */
export const triggerSessionExpiredLogout = async ({ broadcast = true }: { broadcast?: boolean } = {}) => {
    console.log('[session-auth] triggerSessionExpiredLogout called', {
        broadcast,
        inFlight: sessionExpiredLogoutInFlight
    });
    if (sessionExpiredLogoutInFlight) {
        console.log('[session-auth] Logout already in progress - ignoring duplicate trigger');
        return;
    }
    // Only a logged-in session can expire. Guards against pre-login 401s
    // (e.g. wrong credentials, forgot-password) wrongly triggering a logout.
    if (!store.getState()?.auth?.isAuthenticated) {
        console.log('[session-auth] Not authenticated - skipping session-expired logout');
        return;
    }
    sessionExpiredLogoutInFlight = true;
    console.log('[session-auth] Starting session-expired logout flow');

    sessionStorage.setItem('isLoggingOut', 'true');
    store.dispatch(toggleSessionExpiredModal(true));

    // Notify other open tabs so they log out too.
    if (broadcast) {
        console.log('[session-auth] Broadcasting session-expired logout to other tabs');
        authChannel?.postMessage({ type: SESSION_EXPIRED_LOGOUT });
    }

    try {
        await oktaAuth.signOut({
            clearTokensBeforeRedirect: true,
            postLogoutRedirectUri: window.location.origin + '/login',
            revokeAccessToken: true
        });
        console.log('[session-auth] Okta signOut succeeded - clearing redux state and redirecting to /login');
        clearAllSliceState();
    } catch (error) {
        console.error('Error during session-expired sign out:', error);
        console.log('[session-auth] Okta signOut failed - falling back to hard redirect to /login', error);
        clearAllSliceState();
        // Allow a future attempt in case the hard redirect below is blocked.
        sessionExpiredLogoutInFlight = false;
        window.location.assign('/login');
    }
};

export const isJwtExpiredError = (error: any): boolean => {
    // Fallback for non-standard error shapes that omit the status code.
    const data = error?.response?.data;
    if (!data) return false;
    return data?.name === 'TokenExpiredError' || data?.message === 'jwt expired';
};
